/// <amd-module name="Repos/Views/PullRequest/Components/ActivityFeed/ActivityCards/RequiredReviewerPolicyCard" />
import * as React from "react";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getFileName } from "Repos/Common/Util/File";
import { getPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { useComponentContext } from "VSS/Platform/Layout";
import { IBasicIdentityRef } from "VSSUI/Persona";
import { getIdentityComponent } from "../Utils/ActivityFeedUtils";
import { ActivityCardSubtle } from "./ActivityCardSubtle";

export interface IRequiredReviewerPolicyCardProps {
    identities: IBasicIdentityRef[];
    numReviewers: number;
    filePath: string;
    numFiles: number;
    areReviewersRequired: boolean;
    publishDate: Date;
    message: string;
}

const OneReviewerFormats = [
    Resources.ActivityFeedOptionalOneReviewerZeroFiles,
    Resources.ActivityFeedOptionalOneReviewerOneFile,
    Resources.ActivityFeedOptionalOneReviewerTwoFiles,
    Resources.ActivityFeedOptionalOneReviewerManyFiles,
    Resources.ActivityFeedRequiredOneReviewerZeroFiles,
    Resources.ActivityFeedRequiredOneReviewerOneFile,
    Resources.ActivityFeedRequiredOneReviewerTwoFiles,
    Resources.ActivityFeedRequiredOneReviewerManyFiles
];
const TwoReviewerFormats = [
    Resources.ActivityFeedOptionalTwoReviewersZeroFiles,
    Resources.ActivityFeedOptionalTwoReviewersOneFile,
    Resources.ActivityFeedOptionalTwoReviewersTwoFiles,
    Resources.ActivityFeedOptionalTwoReviewersManyFiles,
    Resources.ActivityFeedRequiredTwoReviewersZeroFiles,
    Resources.ActivityFeedRequiredTwoReviewersOneFile,
    Resources.ActivityFeedRequiredTwoReviewersTwoFiles,
    Resources.ActivityFeedRequiredTwoReviewersManyFiles
];
const ManyReviewerFormats = [
    Resources.ActivityFeedOptionalManyReviewersZeroFiles,
    Resources.ActivityFeedOptionalManyReviewersOneFile,
    Resources.ActivityFeedOptionalManyReviewersTwoFiles,
    Resources.ActivityFeedOptionalManyReviewersManyFiles,
    Resources.ActivityFeedRequiredManyReviewersZeroFiles,
    Resources.ActivityFeedRequiredManyReviewersOneFile,
    Resources.ActivityFeedRequiredManyReviewersTwoFiles,
    Resources.ActivityFeedRequiredManyReviewersManyFiles
];

export const RequiredReviewerPolicyCard: React.FunctionComponent<IRequiredReviewerPolicyCardProps> = props => {
    const { areReviewersRequired, filePath, identities, message, numFiles, numReviewers, publishDate } = props;

    if (!identities || !identities.length) {
        return <ActivityCardSubtle createDate={publishDate}>{Resources.ActivityFeedGenericReviewersPolicy}</ActivityCardSubtle>;
    }

    const reviewerComponent = getIdentityComponent(identities[0], "activity-card-first-image");

    const pageContext = useComponentContext().pageContext;
    const reposRoutingService = getReposRoutingService(pageContext);
    const prDetailsService = getPullRequestViewService(pageContext);
    const fileLink = reposRoutingService.getPullRequestUrl(
        prDetailsService.getTargetRepository().name,
        prDetailsService.getTargetRepository().project.name,
        prDetailsService.getId(),
        "files",
        filePath
    );
    const fileComponent = <FPSLink href={fileLink}>{getFileName(filePath)}</FPSLink>;

    const formatIndex = (areReviewersRequired ? 1 : 0) * 4 + Math.min(numFiles, 3);

    let content;

    if (numReviewers <= 1) {
        content = (
            <FormatComponent elementType={null} format={OneReviewerFormats[formatIndex]}>
                {reviewerComponent}
                {fileComponent}
                {(numFiles - 1).toString()}
            </FormatComponent>
        );
    } else if (numReviewers == 2) {
        content = (
            <FormatComponent elementType={null} format={TwoReviewerFormats[formatIndex]}>
                {reviewerComponent}
                {getIdentityComponent(identities[1], "margin-horizontal-8")}
                {fileComponent}
                {(numFiles - 1).toString()}
            </FormatComponent>
        );
    } else {
        content = (
            <FormatComponent elementType={null} format={ManyReviewerFormats[formatIndex]}>
                {reviewerComponent}
                {(numReviewers - 1).toString()}
                {fileComponent}
                {(numFiles - 1).toString()}
            </FormatComponent>
        );
    }

    if (message) {
        content = (
            <>
                {content}
                &nbsp;
                {message}
            </>
        );
    }

    return <ActivityCardSubtle createDate={publishDate}>{content}</ActivityCardSubtle>;
};
